import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  activeInactiveRole,
  deleteRole,
  getAllRoles,
  getRolesFields,
  IRoles,
} from "./RolesViewController";
import { IPagination } from "../../data/AppInterface";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import {
  AUDIT_LOG_TYPE,
  BIT_VALUE,
  STATIC_ROLE,
} from "../../utils/AppEnumerations";
import {
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import {
  deleteIcon,
  editIcon,
  eyeIcon,
  timeLineIcon,
} from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { ORDER_BY } from "../../utils/AppConstants";
import { routeAdmin, routeMaster } from "../../routes/AuthRoutePath";
import { useNavigate } from "react-router-dom";
import AuditLog from "../audit-log/AuditLogView";

let timer: ReturnType<typeof setTimeout>;

const RolesView = () => {
  const { t } = UseTranslationHook();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const { pagination, setChangePagination } = UsePaginationHook();
  const [rolesList, setRolesList] = useState<IRoles[]>([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [roleToView, setRoleToView] = useState<IRoles>();
  useEffect(() => {
    fetchRoles({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch]);

  const disabledRole = Object.values(STATIC_ROLE);

  const fetchRoles = (paginationPlayLoad: IPagination) => {
    setIsLoading(true);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      await getAllRoles(
        setRolesList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const handleRolesStatus = async (values: IRoles) => {
    const isStatusChange =
      values.is_deleted === BIT_VALUE.Zero &&
      (await activeInactiveRole(
        values.id,
        values.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One,
      ));
    if (isStatusChange) {
      setRolesList(toggleStatus(rolesList, values.id));
    }
  };

  const handleDisabledStatus = (item: IRoles) => {
    return disabledRole.map((roleId) => roleId === item.id).includes(true);
  };

  const handleDelete = async (value: boolean) => {
    if (value && roleToView) {
      const data = await deleteRole(roleToView.id);
      if (data) {
        fetchRoles(pagination);
      }
    }
    setShowDeleteConfirmation(false);
  };
  const handleCloseForm = (status: boolean) => {
    setShowAuditLog(false);
    if (roleToView) {
      setRoleToView(undefined);
    }
  };
  const customActionView = (item: IRoles) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            navigate(
              "/" +
                routeMaster.admin +
                "/" +
                routeAdmin.rolesForm +
                "/" +
                item.id,
            );
          }}
        >
          {eyeIcon}
        </div>

        {!handleDisabledStatus(item) && (
          <>
            <div
              className="ml-2 size-5 cursor-pointer"
              title={t("edit_tooltip_icon")}
              onClick={() => {
                navigate(
                  "/" +
                    routeMaster.admin +
                    "/" +
                    routeAdmin.rolesForm +
                    "/" +
                    item.id +
                    "?isEdit=true",
                );
              }}
            >
              {editIcon}
            </div>
            {item.is_deleted === BIT_VALUE.Zero && (
              <div
                className="ml-2 size-5 cursor-pointer"
                title={t("delete_tooltip_icon")}
                onClick={() => {
                  setShowDeleteConfirmation(true);
                  setRoleToView(item);
                }}
              >
                {deleteIcon}
              </div>
            )}
            <div
              className="ml-2 size-5 cursor-pointer"
              title={t("audit_log_tooltip_icon")}
              onClick={() => {
                setRoleToView(item);
                setShowAuditLog(true);
              }}
            >
              {timeLineIcon}
            </div>
          </>
        )}
      </div>
    );
  };
  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() =>
          navigate("/" + routeMaster.admin + "/" + routeAdmin.rolesForm)
        }
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchRoles(pagination);
        }}
        title={t("roles")}
      />

      <ICTable<IRoles>
        isLoading={isLoading}
        totalFlex="grid grid-cols-8"
        fields={getRolesFields(
          customActionView,
          handleRolesStatus,
          handleDisabledStatus,
          t,
        )}
        data={rolesList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchRoles({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchRoles({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchRoles({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />
      {showAuditLog && roleToView && (
        <AuditLog
          handleClose={handleCloseForm}
          auditLogRecordId={roleToView.id}
          auditLogType={AUDIT_LOG_TYPE.Role}
          titleName={t("role")}
        />
      )}
      {showDeleteConfirmation && roleToView && (
        <ICConfirmationModal
          title={t("delete_role")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", roleToView.role_name],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default RolesView;
